// frontend/src/pages/Analytics.jsx
import { useState, useEffect, useCallback } from 'react';
import { adminAPI, investmentAPI, walletAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Card, Button, Badge, StatCard, Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';

const CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-bg-panel border border-border rounded-xl px-3 py-2 text-xs">
      <p className="text-text-muted mb-1">{label}</p>
      <p className="text-cyan font-bold">${payload[0]?.value?.toFixed(2)}</p>
    </div>
  );
};

export default function Analytics() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState([]);
  const [investmentFunds, setInvestmentFunds] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [myInvestments, setMyInvestments] = useState(null);
  const [period, setPeriod] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        investmentAPI.getMyInvestments(),
      ];
      if (isAdmin) {
        promises.push(adminAPI.getRevenueReport({ days: period }));
        promises.push(adminAPI.getInvestmentReport());
        promises.push(adminAPI.getDashboard());
      }

      const results = await Promise.allSettled(promises);

      if (results[0].status === 'fulfilled') {
        setMyInvestments(results[0].value.summary || null);
      }
      if (isAdmin) {
        if (results[1].status === 'fulfilled') setRevenue(results[1].value.revenue || []);
        if (results[2].status === 'fulfilled') setInvestmentFunds(results[2].value.funds || []);
        if (results[3].status === 'fulfilled') setAdminStats(results[3].value.stats || null);
      }
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="xl" /></div>;

  const inv = myInvestments || { totalInvested: 0, totalReturns: 0, activeCount: 0, totalWithdrawn: 0 };

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">Analytics & Reports</h1>
          <p className="text-text-secondary text-sm mt-1">Portfolio performance and platform metrics.</p>
        </div>
        <button onClick={fetchData} className="text-text-muted hover:text-cyan transition-colors p-2 rounded-xl hover:bg-cyan/10">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Personal investment summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Invested"   value={`$${inv.totalInvested?.toFixed(2) || '0.00'}`}  sub="Capital deployed"    color="#8B5CF6" icon="🏗️"/>
        <StatCard label="Returns Earned"   value={`$${inv.totalReturns?.toFixed(2) || '0.00'}`}   sub="Monthly income"      color="#10B981" icon="💸"/>
        <StatCard label="Active Funds"     value={inv.activeCount || 0}                            sub="Live positions"      color="#00A8DB" icon="📊"/>
        <StatCard label="Withdrawn"        value={`$${inv.totalWithdrawn?.toFixed(2) || '0.00'}`} sub="All-time withdrawals" color="#EAB308" icon="📅"/>
      </div>

      {/* Return on investment indicator */}
      {inv.totalInvested > 0 && (
        <Card>
          <SectionHeader title="Portfolio Performance" />
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-bg-panel rounded-xl p-4 border border-border">
              <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-1">ROI</p>
              <p className="text-green font-black text-2xl">
                {((inv.totalReturns / inv.totalInvested) * 100).toFixed(1)}%
              </p>
              <p className="text-text-secondary text-xs mt-1">Returns vs capital</p>
            </div>
            <div className="bg-bg-panel rounded-xl p-4 border border-border">
              <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-1">Net Profit</p>
              <p className="text-cyan font-black text-2xl">
                ${(inv.totalReturns - inv.totalWithdrawn).toFixed(2)}
              </p>
              <p className="text-text-secondary text-xs mt-1">Retained earnings</p>
            </div>
            <div className="bg-bg-panel rounded-xl p-4 border border-border">
              <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-1">Portfolio Value</p>
              <p className="text-purple font-black text-2xl">
                ${(inv.totalInvested + inv.totalReturns - inv.totalWithdrawn).toFixed(2)}
              </p>
              <p className="text-text-secondary text-xs mt-1">Capital + retained returns</p>
            </div>
          </div>
        </Card>
      )}

      {/* Admin-only sections */}
      {isAdmin && (
        <>
          {/* Admin stats */}
          {adminStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Total Users"       value={adminStats.users?.total || 0}                             sub={`${adminStats.users?.active || 0} active`}       color="#00A8DB" icon="👤"/>
              <StatCard label="Pending KYC"       value={adminStats.users?.pendingKYC || 0}                        sub="Awaiting review"                                  color="#EAB308" icon="🔖"/>
              <StatCard label="Total Deposits"    value={`$${(adminStats.revenue?.totalDeposits || 0).toFixed(0)}`}  sub="Platform total"                                color="#10B981" icon="💰"/>
              <StatCard label="Total Investments" value={`$${(adminStats.revenue?.totalInvestments || 0).toFixed(0)}`} sub="Capital deployed"                            color="#8B5CF6" icon="📈"/>
            </div>
          )}

          {/* Revenue chart */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-text-primary font-bold text-sm">Deposit Revenue</h3>
              <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setPeriod(d)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                      period === d ? 'bg-cyan/20 text-cyan border border-cyan/40' : 'bg-bg-panel text-text-muted border border-border'
                    }`}
                  >{d}d</button>
                ))}
              </div>
            </div>
            {revenue.length === 0 ? (
              <Empty icon="📊" title="No revenue data" description={`No deposits in the last ${period} days.`} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenue}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00A8DB" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#00A8DB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E3454" />
                  <XAxis dataKey="_id" tick={{ fontSize: 10, fill: '#4E677E' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#4E677E' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CUSTOM_TOOLTIP />} />
                  <Area type="monotone" dataKey="total" stroke="#00A8DB" fill="url(#revGrad)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Investment fund breakdown */}
          {investmentFunds.length > 0 && (
            <Card>
              <SectionHeader title="Investment Fund Performance" />
              <div className="overflow-x-auto">
                <Table headers={['Fund', 'Total Raised', 'Investors', 'Returns Paid', 'Monthly Rate']}>
                  {investmentFunds.map((f) => (
                    <tr key={f._id}>
                      <Td className="font-semibold text-text-primary">{f.name}</Td>
                      <Td className="text-cyan font-bold">${f.totalRaised?.toLocaleString() || '0'}</Td>
                      <Td>{f.totalInvestors || 0}</Td>
                      <Td className="text-green font-bold">${f.totalReturnsPaid?.toFixed(2) || '0.00'}</Td>
                      <Td>
                        <Badge label={`${f.minMonthlyReturn}–${f.maxMonthlyReturn}%`} color="purple" />
                      </Td>
                    </tr>
                  ))}
                </Table>
              </div>
            </Card>
          )}

          {/* Revenue bar chart */}
          {revenue.length > 0 && (
            <Card>
              <SectionHeader title="Daily Deposit Volume" />
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E3454" />
                  <XAxis dataKey="_id" tick={{ fontSize: 9, fill: '#4E677E' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#4E677E' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CUSTOM_TOOLTIP />} />
                  <Bar dataKey="total" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </>
      )}

      {!isAdmin && (
        <Card>
          <div className="flex flex-col items-center py-8 text-center">
            <div className="text-4xl mb-3">📊</div>
            <h3 className="text-text-primary font-semibold mb-2">Platform Analytics</h3>
            <p className="text-text-muted text-sm max-w-xs">Full platform revenue reports and fund analytics are available to administrators only.</p>
          </div>
        </Card>
      )}
    </div>
  );
}
